import { useState, ReactNode } from "react";
import { ChevronRight, Copy, Check } from "lucide-react";
import { cn } from "../../lib/utils";

export function CodeBlock({ children, ...props }: { children?: ReactNode }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const wrapper = (e.currentTarget as HTMLElement).parentElement;
    const pre = wrapper?.querySelector("pre");
    const text = pre?.textContent || "";

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="code-block-wrapper relative group my-5">
      <pre
        {...props}
        className="rounded-lg overflow-hidden p-4 overflow-x-auto text-[13px] leading-relaxed font-mono text-zinc-800 dark:text-zinc-200 bg-[#F9F8F7] dark:bg-zinc-800"
      >
        {children}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute right-3 top-3 z-10 p-1.5 rounded-md bg-zinc-200 hover:bg-zinc-300 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

export function Callout({
  children,
  variant = "info",
}: {
  children?: ReactNode;
  variant?: "info" | "warning";
}) {
  return (
    <div
      className={cn(
        "my-5 flex items-start gap-3 rounded-lg border p-4",
        variant === "warning"
          ? "border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20"
          : "border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50",
      )}
    >
      <span
        className={cn(
          "text-[14px] leading-[1.7] shrink-0",
          variant === "warning"
            ? "text-amber-500"
            : "text-zinc-400 dark:text-zinc-500",
        )}
      >
        {variant === "warning" ? "\u26A0" : "\u24D8"}
      </span>
      <div className="text-[14px] leading-[1.7] text-zinc-600 dark:text-zinc-400 [&_p]:!m-0 [&_a]:underline [&_a]:underline-offset-2">
        {children}
      </div>
    </div>
  );
}

export function Cards({ children }: { children?: ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 my-6">
      {children}
    </div>
  );
}

export function Card({
  title,
  href,
  children,
}: {
  title: string;
  href: string;
  children?: ReactNode;
}) {
  const hashHref = href.startsWith("/") ? `#${href.slice(1)}` : href;

  return (
    <a
      href={hashHref}
      className="group block p-4 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-all no-underline"
    >
      <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 group-hover:text-zinc-700 dark:group-hover:text-white flex items-center gap-1">
        {title}
        <ChevronRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
      </span>
      {children && (
        <span className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 block [&>p]:m-0">
          {children}
        </span>
      )}
    </a>
  );
}

export const baseMdxComponents = {
  pre: CodeBlock,
  Callout,
  Cards,
  Card,
};
